import React, { ReactElement, useEffect, useState, useMemo, useCallback } from "react";
import {
    QualityHubTaskGroupFormDrawerProps,
    QualityHubTaskGroupFormDrawerState,
    QualityHubTaskGroupFormDrawerStateProps,
} from "./QualityHubTaskGroupFormDrawer.types";
import { TaskGroupModel, createTaskListGroupAsync, updateTaskListGroupAsync } from "src/api";
import { Drawer, DrawerHeader, DrawerHeaderTitle, DrawerBody, DrawerFooter } from "src/components/Drawer";
import { QualityHubTaskGroupForm } from "../QualityHubTaskGroupForm";
import { useDispatch, useSelector } from "react-redux";
import { localize } from "src/l10n";
import { Conditional } from "src/components/Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { PrimaryButton, DefaultButton } from "@fluentui/react";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { taskListGroupCreated, taskListGroupUpdated, findTaskList } from "src/redux";
import { setConfirmPopup } from "src/popups/actions";
import { TaskGroupModelUpdater } from "../types";

/**
 * Gets the initial model for the task group.
 *
 * @param {number} listId - The ID of the task list
 * @return {TaskGroupModel} The initial task group model
 */
function getInitialModel(listId: number): TaskGroupModel {
    return {
        name: "",
        keyConsideration: "",
        listId,
        sortOrder: 0,
    }
}

/**
 * QualityHubTaskGroupFormDrawer component
 *
 * @param {QualityHubTaskGroupFormDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskGroupFormDrawer({
    groupId,
    isOpen,
    localItem,
    onDismiss,
    onLocalSave,
    taskListId: listId,
}: QualityHubTaskGroupFormDrawerProps): ReactElement | null {
    const [model, setModel] = useState<TaskGroupModel>(getInitialModel(listId));
    const [state, setState] = useState<QualityHubTaskGroupFormDrawerState>({
        isDirty: false,
        isSaving: false,
    });

    const {
        group,
        isLoading,
        taskList,
    } = useSelector<Spintr.AppState, QualityHubTaskGroupFormDrawerStateProps>(
        (appState) => ({
            group: !groupId
                ? undefined
                : appState.qualityHub.listsById[listId]?.groups?.find(
                    (g) => g.id === groupId,
                ),
            isLoading: appState.qualityHub.listsLoading[listId] || false,
            taskList: appState.qualityHub.listsById[listId],
        })
    );

    const dispatch = useDispatch();
    const isNew = !groupId;
    const { isSaving } = state;

    const l10n = useMemo(() => ({
        createNew: localize("ADD_GROUP"),
        edit: localize("Redigera"),
        cancel: localize("Avbryt"),
        save: localize("Spara"),
        saveAndContinue: localize("SparaOchFortsatt"),
    }), []);

    const onDismissing = useCallback(() => {
        if (!state.isDirty) {
            setModel(getInitialModel(listId));
            onDismiss(false);
            return;
        }

        dispatch(setConfirmPopup({
            isOpen: true,
            message: localize("UnsavedChangesWarning"),
            onConfirm: () => {
                setModel(getInitialModel(listId));
                setState((state) => ({ ...state, isDirty: false }));
                onDismiss(false);
            },
        }));
    }, [state.isDirty, onDismiss, setModel, setState]);
    
    const onCancelClicked = useCallback(onDismissing, [onDismissing]);
    const onOpenChanged = useCallback((open: boolean) => {
        if (!open) {
            onDismissing();
        }
    }, [onDismissing]);

    const onSubmitting = useCallback(async (closeOnSuccess?: boolean) => {
        if (isSaving) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isSaving: true,
        }));

        let group: Spintr.TaskListGroup | undefined;
        if (onLocalSave) {
            onLocalSave(model);
        } else {
            try {
                group = await (!groupId
                    ? createTaskListGroupAsync(model)
                    : updateTaskListGroupAsync(groupId, model));
            } catch (err) {
                console.error(err);
                setState((prevState) => ({
                    ...prevState,
                    isSaving: false,
                }));
                return;
            }
        }

        if (group) {
            dispatch(!groupId
                ? taskListGroupCreated(group)
                : taskListGroupUpdated(group));
        }

        setState((prevState) => ({
            ...prevState,
            isSaving: false,
        }));
        setModel(getInitialModel(listId));

        if (!closeOnSuccess) {
            return;
        }

        onDismiss(true, group);
    }, [groupId, model, dispatch, isSaving, onDismiss, listId]);

    const submitAndCloseOnSuccess = useCallback(
        () => onSubmitting(true),
        [onSubmitting]);

    const submitAndKeepOpen = useCallback(
        () => onSubmitting(false),
        [onSubmitting]);

    useEffect(() => {
        if (!groupId) {
            setState((state) => ({ ...state, isDirty: false }));
            setModel(getInitialModel(listId));
            return;
        }

        if (!taskList) {
            dispatch(findTaskList(listId));
            return;
        }

        const groupToUse = localItem || group;
        if (!groupToUse) {
            // TODO: Should probably call onDismiss here
            return;
        }

        setState((state) => ({ ...state, isDirty: false }));
        setModel({
            ...groupToUse,
            name: groupToUse.name,
            keyConsideration: groupToUse.keyConsideration || "",
            listId: groupToUse.listId,
            sortOrder: groupToUse.sortOrder,
            startTime: groupToUse.startTime,
            endTime: groupToUse.endTime,
        });
    }, [groupId, group, localItem]);

    const onModelChanging = useCallback<TaskGroupModelUpdater>((updater) => {
        setModel((model) => updater(model));
        setState((state) => ({ ...state, isDirty: true }));
    }, [setModel, setState]);

    return (
        <Drawer className="QualityHubTaskGroupFormDrawer"
                onOpenChange={onOpenChanged}
                open={isOpen}
                position="end"
                size="medium">
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {isNew ? l10n.createNew : l10n.edit}
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody>
                <Conditional condition={isLoading || isSaving}>
                    <SpintrLoader />
                </Conditional>
                <Conditional condition={!isLoading && !isSaving}>
                    <QualityHubTaskGroupForm isCustomizing={!!onLocalSave}
                                             model={model}
                                             onModelChange={onModelChanging} />
                </Conditional>
            </DrawerBody>

            <DrawerFooter className="QualityHubTaskGroupFormDrawer-footer">
                <UnstyledButton disabled={isSaving || isLoading}
                                onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <div className="save-buttons">
                    <Conditional condition={isNew}>
                        <DefaultButton className="QualityHubTaskFormDrawer-saveAndClose"
                                       disabled={isSaving || isLoading}
                                       onClick={submitAndCloseOnSuccess}
                                       type="submit">
                            {l10n.save}
                        </DefaultButton>
                    </Conditional>
                    <PrimaryButton className="QualityHubTaskFormDrawer-save"
                                   disabled={isSaving || isLoading}
                                   onClick={isNew ? submitAndKeepOpen : submitAndCloseOnSuccess}
                                   type="submit">
                        {isNew ? l10n.saveAndContinue : l10n.save}
                    </PrimaryButton>
                </div>
            </DrawerFooter>
        </Drawer>
    );
}

export default QualityHubTaskGroupFormDrawer;

